/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { config } from '@/src/config/app-config';
import { NewAiEventPage } from '@/src/pages/events/ai';
import { UserGuidePage } from '@/src/pages/user-guide';
import { isArray } from 'lodash';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { RoutePath } from './RoutePath';
import { MY_JAM_PREFIX } from './constants/api';
import { AuthRole } from './constants/shared/auth-roles';
import { HomePage } from './pages';
import { JamEventPage } from './pages/$eventId';
import { JamEventFacilitatorMessagingPage } from './pages/$eventId/facilitator/messaging';
import { JamEventNotificationsPage } from './pages/$eventId/facilitator/notifications';
import { JamEventFacilitatorParticipantsPage } from './pages/$eventId/facilitator/participants';
import { JamEventSettingsPage } from './pages/$eventId/facilitator/settings';
import { JamEventSupportChatsPage } from './pages/$eventId/facilitator/support-chats';
import { JamEventMessagesPage } from './pages/$eventId/messages';
import { JamEventOnboardingPage } from './pages/$eventId/onboarding';
import { JamEventTeamJoinPage as JamEventJoinTeamPage } from './pages/$eventId/onboarding/$joinOption';
import { JamEventTeamPage } from './pages/$eventId/team';
import { ActiveEventsPage } from './pages/active-events';
import { CampaignsPage } from './pages/campaigns';
import { CampaignDetailsPage } from './pages/campaigns/$campaignId';
import { CampaignGroupDetailsPage } from './pages/campaigns/$campaignId/groups/$eventName';
import { NewCampaignPage } from './pages/campaigns/new';
import { ChallengeSetsPage } from './pages/challenge-set';
import { ChallengeSetDetailsPage } from './pages/challenge-set/$challengeSetId';
import { NewChallengeSetPage } from './pages/challenge-set/new';
import { ChallengesPage } from './pages/challenges';
import { ChallengeResourcesPage } from './pages/challenges-resources/$challengeId';
import { ChallengesSnippetsPage } from './pages/challenges-snippets';
import { ChallengesSshAccessPage } from './pages/challenges-ssh-access';
import { ChallengeDetailsPage } from './pages/challenges/$challengeId';
import { NewChallengePage } from './pages/challenges/new';
import { CreateJamProfilePage } from './pages/create-jam-profile';
import { DocumentationPage } from './pages/documentation';
import { EventDetailsPage } from './pages/events/$eventName';
import { NewEventPage } from './pages/events/new';
import { FaqPage } from './pages/faq';
import { FaqSectionPage } from './pages/faq/$section';
import { MyJamsPage } from './pages/jam';
import { JoinAnEventPage } from './pages/join-an-event';
import { LabsPage } from './pages/labs';
import { MyEventsPage } from './pages/my-events';
import { OAuthRedirectPage } from './pages/oauth2';
import { PastEventsPage } from './pages/past-events';
import { CreateProfilePage } from './pages/profile/create';
import { ProfileDetailsPage } from './pages/profile/detail';
import { ProfileSkillDetailsPage } from './pages/profile/skill-detail';
import { ReportsPage } from './pages/reports';
import { ReviewChallengesPage } from './pages/review-challenges';
import { SettingsPage } from './pages/settings';
import { UpcomingEventsPage } from './pages/upcoming-events';
import { i18nKeys, withI18NPrefix } from './utils/i18n.utils';

export interface RouteItem {
  path: string;
  name: string;
  requiredGroups?: AuthRole[];
  Component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
  exact?: boolean;
  disabled?: boolean;
  entityType?:
    | 'event'
    | 'challenge'
    | 'challengeSet'
    | 'campaign'
    | 'group'
    | 'eventTemplate'
    | 'catalog'
    | 'faq'
    | 'jam-event-onboarding'
    | 'jam-event-onboarding-options'
    | 'jam-challenges';
}

export class ResolvableRoute {
  public static readonly TOKEN = '{token}';

  public constructor(private readonly path: string) {
    if (!path.includes(ResolvableRoute.TOKEN)) {
      throw new Error(`Path '${path}' is non-resolvable`);
    }
  }

  replaceTokens = (tokens: string[]) => {
    let newPath = this.path;
    tokens.forEach((token) => {
      newPath = newPath.replace(ResolvableRoute.TOKEN, token);
    });
    return newPath;
  };

  public resolve(tokenParams: string | string[]): string {
    if (isArray(tokenParams)) {
      return this.replaceTokens(tokenParams);
    } else {
      return this.path.replaceAll(ResolvableRoute.TOKEN, tokenParams);
    }
  }

  public wildcard(): string {
    return this.resolve('*');
  }
}

export const CHALLENGE_DETAILS_ROUTES = {
  Summary: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}`),
  LearningOutcome: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/learning-outcome`),
  Settings: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/settings`),
  ArenaScenario: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/scenario`),
  ArenaRequirements: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/requirements`),
  ArenaDimensions: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/dimensions`),
  Tasks: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/tasks`),
  Assets: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/assets`),
  IamPolicy: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/iam`),
  ShowIamPolicy: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/show-i-am`),
  CfnTemplate: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/cfn`),
  NextSteps: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/next-steps`),
  Wiki: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/wiki`),
  Collaborators: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/collaborators`),
  Issues: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/issues`),
  IssuesSingle: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/issues/${ResolvableRoute.TOKEN}`),
  Comments: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/comments`),
  Testing: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/testing`),
  Feedback: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/feedback`),
  Stats: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/stats`),
  Revisions: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/revisions`),
  Reviews: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/review/`),
  Translation: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/translation`),
  FacilitatorNotes: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/facilitator-notes`),
  Help: new ResolvableRoute(`${RoutePath.CHALLENGES}/${ResolvableRoute.TOKEN}/help`),
} as const;

export const EVENT_DETAILS_ROUTES = {
  Summary: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}`),
  Challenges: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}/challenges`),
  Settings: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}/settings`),
  Comments: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}/comments`),
  Participants: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}/participants`),
  ParticipantInvites: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}/participant-invites`),
  ParticipantsV1: new ResolvableRoute(`/v1/events/${ResolvableRoute.TOKEN}/participants`),
  OwnerAndPermission: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}/owner-and-permissions`),
  Labs: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}/labs`),
  Feedback: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}/feedback`),
  Revisions: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}/revisions`),
  Report: new ResolvableRoute(`/events/${ResolvableRoute.TOKEN}/report`),
} as const;

export const CHALLENGE_SETS_ROUTES = {
  Summary: new ResolvableRoute(`/challenge-set/${ResolvableRoute.TOKEN}`),
};

export const CAMPAIGN_DETAILS_ROUTES = {
  Summary: new ResolvableRoute(`/campaigns/${ResolvableRoute.TOKEN}`),
  Challenges: new ResolvableRoute(`/campaigns/${ResolvableRoute.TOKEN}/challenges`),
  Settings: new ResolvableRoute(`/campaigns/${ResolvableRoute.TOKEN}/settings`),
  AdvancedSettings: new ResolvableRoute(`/campaigns/${ResolvableRoute.TOKEN}/advanced-settings`),
  Groups: new ResolvableRoute(`/campaigns/${ResolvableRoute.TOKEN}/groups`),
  NewGroup: new ResolvableRoute(`/campaigns/${ResolvableRoute.TOKEN}/groups/new`),
  GroupDetails: new ResolvableRoute(`/campaigns/${ResolvableRoute.TOKEN}/groups/${ResolvableRoute.TOKEN}`),
  Comments: new ResolvableRoute(`/campaigns/${ResolvableRoute.TOKEN}/comments`),
};

export const JAM_EVENT_DETAILS_ROUTES = {
  Landing: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}`),
  Onboarding: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/onboarding`),
  JoinTeam: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/onboarding/${ResolvableRoute.TOKEN}`),
  Dashboard: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/dashboard`),
  LeaderBoard: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/leaderboard`),
  Challenges: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/challenges`),
  Team: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/team`),
  Feedback: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/feedback`),
  Report: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/report`),
  Messages: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/messages`),
  Meetings: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/meetings`),
  Facilitator: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/facilitator`),
  TeamChat: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/team/chat`),
  TeamInfo: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/team/${ResolvableRoute.TOKEN}`),
  ConversationalAI: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/teammate`),
} as const;

export const JAM_CHALLENGE_DETAILS_ROUTES = {
  Overview: new ResolvableRoute(
    `${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/challenges/${ResolvableRoute.TOKEN}/overview`
  ),
  RecentFeedback: new ResolvableRoute(
    `${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/challenges/${ResolvableRoute.TOKEN}/recent-feedback`
  ),
  Issue: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/challenges/${ResolvableRoute.TOKEN}/issue`),
  FacilitatorNotes: new ResolvableRoute(
    `${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/challenges/${ResolvableRoute.TOKEN}/facilitator-notes`
  ),
  Wiki: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/challenges/${ResolvableRoute.TOKEN}/wiki`),
  OutputProperties: new ResolvableRoute(
    `${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/challenges/${ResolvableRoute.TOKEN}/output-properties`
  ),
} as const;

export const JAM_FACILITATOR_DETAILS_ROUTES = {
  Participants: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/facilitator/participants`),
  Messaging: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/facilitator/messaging`),
  SupportChats: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/facilitator/support-chats`),
  JamSettings: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/facilitator/settings`),
  Notifications: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/facilitator/notifications`),
  Support: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/facilitator/support`),
  AuditLog: new ResolvableRoute(`${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/facilitator/action-log`),
};

export const JAM_CHALLENGE_ROUTES = new ResolvableRoute(
  `${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/challenges/${ResolvableRoute.TOKEN}/overview`
);

export const JAM_CHALLENGE_TASK_ROUTES = new ResolvableRoute(
  `${MY_JAM_PREFIX}/${ResolvableRoute.TOKEN}/challenges/${ResolvableRoute.TOKEN}/tasks/${ResolvableRoute.TOKEN}/details`
);

export interface ParameterizedRouteItem extends RouteItem {
  resolvePath: (...params: string[]) => string;
}

export const homeRoute: RouteItem = {
  path: RoutePath.ROOT,
  name: withI18NPrefix(i18nKeys.home.title),
  exact: true,
  Component: HomePage,
};

export const oauth2Route: RouteItem = {
  path: RoutePath.OAUTH2,
  name: 'Oauth',
  exact: true,
  Component: OAuthRedirectPage,
};

export const eventsRoute: RouteItem = {
  path: RoutePath.EVENTS,
  name: withI18NPrefix(i18nKeys.events.title),
  exact: true,
  Component: MyEventsPage,
};

export const myEventsRoute: RouteItem = {
  path: RoutePath.MY_EVENTS,
  name: withI18NPrefix(i18nKeys.events.title),
  exact: true,
  Component: MyEventsPage,
};

export const newEventRoute: RouteItem = {
  path: RoutePath.NEW_EVENT,
  name: withI18NPrefix(i18nKeys.newEvent.title),
  exact: true,
  entityType: 'event',
  Component: NewEventPage,
  requiredGroups: [AuthRole.SUBSCRIBED_USER, AuthRole.SBTS_LEARNER],
};

export const newAiEventRoute: RouteItem = {
  path: RoutePath.NEW_AI_EVENT,
  name: withI18NPrefix(i18nKeys.newEvent.title),
  exact: true,
  entityType: 'event',
  Component: NewAiEventPage,
  requiredGroups: [AuthRole.SUBSCRIBED_USER, AuthRole.SBTS_LEARNER],
  disabled: !config.enableGenAIEventCreate,
};

export const eventRoute: ParameterizedRouteItem = {
  path: RoutePath.EVENT_DETAILS,
  name: withI18NPrefix(i18nKeys.events.event),
  exact: false,
  Component: EventDetailsPage,
  entityType: 'event',
  resolvePath: (...params) => `/events/${params[0] || ''}*`,
};

export const newCampaignRoute: RouteItem = {
  path: RoutePath.NEW_CAMPAIGN,
  name: withI18NPrefix(i18nKeys.campaigns.newCampaign.title),
  exact: true,
  Component: NewCampaignPage,
  requiredGroups: [AuthRole.JAM_ADMIN, AuthRole.EVENT_SUPPORT],
};

export const campaignsRoute: RouteItem = {
  path: RoutePath.CAMPAIGNS,
  name: withI18NPrefix(i18nKeys.campaigns.title),
  exact: true,
  Component: CampaignsPage,
  requiredGroups: [AuthRole.JAM_ADMIN, AuthRole.EVENT_SUPPORT],
};

export const campaignRoute: ParameterizedRouteItem = {
  path: RoutePath.CAMPAIGN_DETAILS,
  name: withI18NPrefix(i18nKeys.campaigns.campaignDetails.title),
  exact: false,
  entityType: 'campaign',
  resolvePath: (...params) => `/campaigns/${params[0] || ''}*`,
  Component: CampaignDetailsPage,
  requiredGroups: [AuthRole.JAM_ADMIN, AuthRole.EVENT_SUPPORT],
};

export const groupRoute: ParameterizedRouteItem = {
  path: RoutePath.CAMPAIGN_GROUP_DETAILS,
  name: withI18NPrefix(i18nKeys.campaigns.headers.groups.group),
  exact: false,
  entityType: 'group',
  resolvePath: (...params) => `/campaigns/${params[0] || ''}/groups/${params[1] || ''}`,
  Component: CampaignGroupDetailsPage,
  requiredGroups: [AuthRole.JAM_ADMIN, AuthRole.EVENT_SUPPORT],
};

export const challengesRoute: RouteItem = {
  path: RoutePath.CHALLENGES,
  name: withI18NPrefix(i18nKeys.challenges.title),
  exact: true,
  Component: ChallengesPage,
  requiredGroups: [
    AuthRole.JAM_ADMIN,
    AuthRole.CHALLENGE_SUPPORT,
    AuthRole.CHALLENGE_REVIEWER,
    AuthRole.CHALLENGE_BUILDER,
    AuthRole.SUBSCRIBED_USER,
    AuthRole.SBTS_LEARNER,
    AuthRole.GUEST_USER,
  ],
};

export const challengeSetsRoute: RouteItem = {
  path: RoutePath.CHALLENGE_SETS,
  name: withI18NPrefix(i18nKeys.challenges.challengeSets),
  exact: true,
  Component: ChallengeSetsPage,
  requiredGroups: [
    AuthRole.JAM_ADMIN,
    AuthRole.CHALLENGE_SUPPORT,
    AuthRole.CHALLENGE_REVIEWER,
    AuthRole.CHALLENGE_BUILDER,
    AuthRole.SUBSCRIBED_USER,
    AuthRole.SBTS_LEARNER,
    AuthRole.GUEST_USER,
  ],
};

export const challengeSetRoute: ParameterizedRouteItem = {
  path: RoutePath.CHALLENGE_SET_DETAILS,
  name: withI18NPrefix(i18nKeys.challenges.challengeSets),
  exact: false,
  Component: ChallengeSetDetailsPage,
  entityType: 'challengeSet',
  resolvePath: (...params) => `/challenge-set/${params[0] ?? ''}*`,
  requiredGroups: [
    AuthRole.JAM_ADMIN,
    AuthRole.CHALLENGE_SUPPORT,
    AuthRole.CHALLENGE_REVIEWER,
    AuthRole.CHALLENGE_BUILDER,
    AuthRole.SUBSCRIBED_USER,
    AuthRole.SBTS_LEARNER,
    AuthRole.GUEST_USER,
  ],
};

export const createChallengeSetRoute: RouteItem = {
  path: RoutePath.NEW_CHALLENGE_SET,
  name: withI18NPrefix(i18nKeys.challenges.challengeSet.header.createChallengeSet),
  exact: true,
  Component: NewChallengeSetPage,
  requiredGroups: [AuthRole.JAM_ADMIN, AuthRole.CHALLENGE_SUPPORT, AuthRole.CHALLENGE_BUILDER],
};

export const reviewChallengesRoute: RouteItem = {
  path: RoutePath.REVIEW_CHALLENGES,
  name: withI18NPrefix(i18nKeys.challenges.reviewChallenges.title),
  exact: true,
  Component: ReviewChallengesPage,
  requiredGroups: [AuthRole.JAM_ADMIN, AuthRole.CHALLENGE_SUPPORT, AuthRole.CHALLENGE_REVIEWER],
};

export const challengeRoute: ParameterizedRouteItem = {
  path: RoutePath.CHALLENGE_DETAILS,
  name: withI18NPrefix(i18nKeys.challenges.challenge),
  exact: false,
  Component: ChallengeDetailsPage,
  entityType: 'challenge',
  resolvePath: (...params) => `/challenges/${params[0] ?? ''}*`,
};

export const createChallengeRoute: RouteItem = {
  path: RoutePath.NEW_CHALLENGE,
  name: withI18NPrefix(i18nKeys.challenges.createChallenge),
  exact: true,
  Component: NewChallengePage,
  requiredGroups: [AuthRole.JAM_ADMIN, AuthRole.CHALLENGE_SUPPORT, AuthRole.CHALLENGE_BUILDER],
};

export const documentationRoute: RouteItem = {
  path: RoutePath.DOCUMENTATION,
  name: withI18NPrefix(i18nKeys.documentation.title),
  exact: true,
  Component: DocumentationPage,
};

export const settingsRoute: RouteItem = {
  path: RoutePath.SETTINGS,
  name: withI18NPrefix(i18nKeys.settings.advancedSettings),
  exact: true,
  Component: SettingsPage,
  requiredGroups: [AuthRole.JAM_ADMIN],
};

export const challengeSnippets: RouteItem = {
  path: RoutePath.CHALLENGE_SNIPPETS,
  name: 'Challenges Snippets',
  exact: true,
  Component: ChallengesSnippetsPage,
};

export const challengeResources: ParameterizedRouteItem = {
  path: RoutePath.CHALLENGES_RESOURCES,
  name: 'Challenges Resources',
  exact: false,
  Component: ChallengeResourcesPage,
  resolvePath: (...params) => `/challenges-resources/${params[0] || ''}`,
};

export const challengeSSHAccess: RouteItem = {
  path: RoutePath.SSH_ACCESS,
  name: 'Challenges SSHAccess',
  exact: true,
  Component: ChallengesSshAccessPage,
};

export const createJamProfileRoute: RouteItem = {
  path: RoutePath.JAM_PROFILE_CREATE,
  name: withI18NPrefix(i18nKeys.createJamProfile.title),
  exact: true,
  Component: CreateJamProfilePage,
};

export const adminRoute: RouteItem = {
  path: RoutePath.ADMIN,
  name: withI18NPrefix(i18nKeys.header.nav.admin.text),
  exact: true,
  Component: SettingsPage,
  requiredGroups: [AuthRole.JAM_ADMIN],
};

export const faqRoute: RouteItem = {
  path: RoutePath.FAQ,
  name: withI18NPrefix(i18nKeys.faq.title),
  exact: true,
  Component: FaqPage,
};

export const userGuideRoute: RouteItem = {
  path: RoutePath.USER_GUIDE,
  name: withI18NPrefix(i18nKeys.userGuide.title),
  exact: true,
  Component: UserGuidePage,
};

export const faqSectionRoute: ParameterizedRouteItem = {
  path: RoutePath.FAQ_SECTION,
  name: withI18NPrefix(i18nKeys.faq.title),
  exact: false,
  Component: FaqSectionPage,
  entityType: 'faq',
  resolvePath: (...params) => `/faq/${params[0] || ''}`,
};

export const myJamsRoute: RouteItem = {
  path: RoutePath.MY_JAMS,
  name: withI18NPrefix(i18nKeys.myJams.title),
  exact: true,
  Component: MyJamsPage,
};

export const myJamsActiveEventsRoute: RouteItem = {
  path: RoutePath.MY_JAMS_ACTIVE,
  name: withI18NPrefix(i18nKeys.myJams.eventTabs.activeEvents),
  exact: true,
  Component: ActiveEventsPage,
};

export const myJamsPastEventsRoute: RouteItem = {
  path: RoutePath.MY_JAMS_PAST,
  name: withI18NPrefix(i18nKeys.myJams.eventTabs.pastEvents),
  exact: true,
  Component: PastEventsPage,
};

export const myJamsUpcomingEventsRoute: RouteItem = {
  path: RoutePath.MY_JAMS_UPCOMING,
  name: withI18NPrefix(i18nKeys.myJams.eventTabs.upcomingEvents),
  exact: true,
  Component: UpcomingEventsPage,
};

export const jamProfile: RouteItem = {
  path: RoutePath.JAM_PROFILE,
  name: withI18NPrefix(i18nKeys.JamProfile.title),
  exact: true,
  Component: ProfileDetailsPage,
};
export const jamProfileDetails: RouteItem = {
  path: RoutePath.JAM_PROFILE_DETAILS,
  name: withI18NPrefix(i18nKeys.JamProfile.jamProfileDetails.title),
  exact: true,
  Component: ProfileDetailsPage,
};

export const userProfileCreate: RouteItem = {
  path: RoutePath.USER_PROFILE_CREATE,
  name: withI18NPrefix(i18nKeys.createJamProfile.title),
  exact: true,
  Component: CreateProfilePage,
};

export const jamSkillProfile: RouteItem = {
  path: RoutePath.JAM_SKILL_PROFILE,
  name: withI18NPrefix(i18nKeys.JamProfile.jamSkillProfile.title),
  exact: true,
  Component: ProfileSkillDetailsPage,
};

export const joinAnEvent: RouteItem = {
  path: RoutePath.JOIN_AN_EVENT,
  name: withI18NPrefix(i18nKeys.myJams.joinAnEvent.title),
  exact: true,
  Component: JoinAnEventPage,
};

export const jamEventRoute: ParameterizedRouteItem = {
  path: RoutePath.JAM_EVENT,
  name: withI18NPrefix(i18nKeys.myJams.title),
  exact: false,
  Component: JamEventPage,
  entityType: 'jam-event-onboarding',
  resolvePath: (...params) => `${MY_JAM_PREFIX}/${params[0] || ''}`,
};

export const jamEventOnboardingRoute: ParameterizedRouteItem = {
  path: RoutePath.JAM_EVENT_ONBOARDING,
  name: withI18NPrefix(i18nKeys.joinTeam.title),
  exact: false,
  Component: JamEventOnboardingPage,
  resolvePath: (...params) => `${MY_JAM_PREFIX}/${params[0] || ''}/onboarding`,
};

export const jamEventOnboardingOptionsRoute: ParameterizedRouteItem = {
  path: RoutePath.JOIN_TEAM,
  name: 'anything',
  exact: false,
  Component: JamEventJoinTeamPage,
  entityType: 'jam-event-onboarding-options',
  resolvePath: (...params) => `${MY_JAM_PREFIX}/${params[0] || ''}/onboarding/${params[1] || ''}`,
};

export const jamEventTeamRoute: ParameterizedRouteItem = {
  path: RoutePath.JAM_EVENT_TEAM,
  name: withI18NPrefix(i18nKeys.jamTeam.title),
  exact: false,
  Component: JamEventTeamPage,
  resolvePath: (...params) => `${MY_JAM_PREFIX}/${params[0] || ''}/team`,
};

export const jamFacilitatorMessagingRoute: ParameterizedRouteItem = {
  path: RoutePath.JAM_FACILITATOR_MESSAGING,
  name: withI18NPrefix(i18nKeys.facilitator.messaging.title),
  exact: false,
  Component: JamEventFacilitatorMessagingPage,
  resolvePath: (...params) => `${MY_JAM_PREFIX}/${params[0] || ''}/facilitator/messaging`,
};

export const jamFacilitatorSupportChatsRoute: ParameterizedRouteItem = {
  path: RoutePath.JAM_FACILITATOR_SUPPORT_CHATS,
  name: withI18NPrefix(i18nKeys.facilitator.supportChats.title),
  exact: false,
  Component: JamEventSupportChatsPage,
  resolvePath: (...params) => `${MY_JAM_PREFIX}/${params[0] || ''}/facilitator/support-chats`,
};

export const jamFacilitatorJamSettingsRoute: ParameterizedRouteItem = {
  path: RoutePath.JAM_FACILITATOR_JAM_SETTINGS,
  name: withI18NPrefix(i18nKeys.facilitator.jamSettings.title),
  exact: false,
  Component: JamEventSettingsPage,
  resolvePath: (...params) => `${MY_JAM_PREFIX}/${params[0] || ''}/facilitator/settings`,
};

export const jamFacilitatorNotificationsRoute: ParameterizedRouteItem = {
  path: RoutePath.JAM_FACILITATOR_NOTIFICATIONS,
  name: withI18NPrefix(i18nKeys.facilitator.notifications.title),
  exact: false,
  Component: JamEventNotificationsPage,
  resolvePath: (...params) => `${MY_JAM_PREFIX}/${params[0] || ''}/facilitator/notifications`,
};

export const jamFacilitatorParticipantsRoute: ParameterizedRouteItem = {
  path: RoutePath.JAM_FACILITATOR_PARTICIPANTS,
  name: withI18NPrefix(i18nKeys.facilitator.participants.title),
  exact: false,
  Component: JamEventFacilitatorParticipantsPage,
  resolvePath: (...params) => `${MY_JAM_PREFIX}/${params[0] || ''}/facilitator/participants`,
};

export const jamMessagesRoute: ParameterizedRouteItem = {
  path: RoutePath.JAM_MESSAGES,
  name: withI18NPrefix(i18nKeys.jamMessages.title),
  exact: false,
  Component: JamEventMessagesPage,
  resolvePath: (...params) => `${MY_JAM_PREFIX}/${params[0] || ''}/messages`,
};

export const reportsRoute: RouteItem = {
  path: RoutePath.REPORTS,
  name: withI18NPrefix(i18nKeys.reports.title),
  exact: true,
  Component: ReportsPage,
};

export const labsRoute: RouteItem = {
  path: RoutePath.LABS,
  name: withI18NPrefix(i18nKeys.labs.title),
  exact: true,
  Component: LabsPage,
};
export const routes: RouteItem[] = [homeRoute];

// Catalog service has been decommissioned - routes removed

routes.push(
  oauth2Route, // /oauth2
  eventsRoute, // events'
  myEventsRoute, // my-events
  newEventRoute, // events/new
  newAiEventRoute, // events/new-ai
  eventRoute, // /events/:eventName'
  newCampaignRoute, // /campaigns/new
  campaignRoute, // /campaigns/:campaignId
  campaignsRoute, // /campaigns'
  createChallengeRoute, // /challenges/new
  challengeRoute, // /challenges/:challengeId
  challengesRoute, // /challenges
  createChallengeSetRoute, // /challenge-set/new
  challengeSetRoute, // /challenge-set/:challengeSetId
  challengeSetsRoute, // /challenge-set
  reviewChallengesRoute, // /review-challenges
  documentationRoute, // //documentation
  settingsRoute, // /setting
  reportsRoute, // /reports
  labsRoute, // /labs
  userProfileCreate, // /profile/create
  jamProfileDetails, // /profile/detail
  jamSkillProfile, // /profile/skill-detail
  jamProfile, // /profile
  createJamProfileRoute, // /create-jam-profile
  adminRoute, // /admin
  faqRoute, // /faq
  faqSectionRoute, // /faq/:section
  myJamsRoute, // /jam
  joinAnEvent, // /join-an-event
  myJamsActiveEventsRoute, // /active-events
  myJamsPastEventsRoute, // /past-events
  myJamsUpcomingEventsRoute, // /upcoming-events
  jamEventRoute, // /:eventId
  challengeSnippets, // /challenges/snippets
  challengeResources, // /challenges-resources
  challengeSSHAccess // /SSH-Access
);

export const rootRoute = routes.find((r) => r.path === '/');
export const resolve404Path = (from: string) => `/404?from=${encodeURIComponent(from)}`;

export default routes;
